'use strict';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.DataModelService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods for retrieving data sources
 */

DataModelService.$inject = [
	'$q',
	'mx.SolutionBuilderAgent.Http',
	'mx.SolutionBuilderAgent.ControlDescriptorService',
	'mx.internationalization',
	'mx.shell.UserProfileService',
	'mx.shell.BackgroundProcessesService',
	'mx.shell.AppsService',
	'mx.shell.HostUrlHelper',
	'mx.SolutionBuilderAgent.mxColumnDefinitionService',
	'mx.shell.DownloadHelpService',
	'mx.SolutionBuilderAgent.FilterExpressionService',
	'mx.SolutionBuilderAgent.SchemaService.Enums',
	'mx.SolutionBuilderAgent.HelpService',
	'$stateParams',
];

export default function DataModelService(
	$q,
	http,
	controlDescriptorService,
	internationalization,
	userProfileService,
	backgroundProcessesService,
	appsService,
	hostUrlHelper,
	columnDefinitionService,
	downloadHelpService,
	filterExpressionService,
	schemaServiceEnums,
	helpService,
	$stateParams
) {
	const cache = { data: {} };

	const _sourceDescriptors = {};

	const sysProperties = mx.SolutionBuilderAgent.DataModelSystemProperty;
	const dataTypes = schemaServiceEnums.DataTypes;

	const dataModelHelper = new mx.SolutionBuilderAgent.DataModelHelper();
	const profiler = dataModelHelper.profiler;

	angular.extend(dataModelHelper.additionalData.config, hostUrlHelper.getHostUrls());

	return {
		// Common
		getSources: getSources,
		getEntityModel: getEntityModel,
		getDataQueryModel: getDataQueryModel,

		// Design time
		getDataModel: getDataModel,
		saveCalculatedField: saveCalculatedField,
		deleteCalculatedField: deleteCalculatedField,
		getInputParametersModel: getInputParametersModel,
		extendDataModelSource: extendDataModelSource,

		// Run time
		loadDialogDataModel: loadDialogDataModel,
		loadDataModel: loadDataModel,
		loadSources: loadSources,
		reloadSource: reloadSource,
		reloadSourceByPath: reloadSourceByPath,

		extendSourceResult: extendSourceResult,
		serializeObject: serializeObject,
		cleanFromEmptyOptionalDataDefinition: cleanFromEmptyOptionalDataDefinition,
	};

	function serializeObject(entity) {
		return JSON.stringify(entity)
			.replace(/\.[0-9]{1,3}Z"/g, '"')
			.replace(/:[0-9]{2}Z"/g, function (x) {
				return x.replace('Z', '');
			});
	}

	function getSources(modelDescriptor, forRuntime, isEmail) {
		const getStaticModelArgument = modelDescriptor.dataQueryId || modelDescriptor.entityName || modelDescriptor.dataDefinition || modelDescriptor.controlDescriptorId || null;
		const getStaticModel =
			(modelDescriptor.dataQueryId && getDataQueryModel) ||
			(modelDescriptor.entityName && getEntityModel) ||
			(modelDescriptor.controlDescriptorId && getCustomWidgetModel) ||
			null;

		if (!Array.isArray(modelDescriptor.Sources)) {
			modelDescriptor.Sources = [];
		}

		if ((!getStaticModel || !getStaticModelArgument) && modelDescriptor.Sources.length === 0) {
			return $q.when([]);
		}

		const sources = modelDescriptor.Sources.map(function (sd) {
			return loadInfo(sd, forRuntime, modelDescriptor);
		});
		const staticSource =
			getStaticModel &&
			getStaticModelArgument &&
			getStaticModel(getStaticModelArgument, false, modelDescriptor.ExtraProperties, modelDescriptor.widgetId).then(function (staticSource) {
				if (staticSource) {
					staticSource.isStatic = true;
					if (!modelDescriptor.isCustomWidget) {
						staticSource.addBoolean('IsNew');
					}
				}
				return staticSource || null;
			});

		if (staticSource) {
			sources.push(staticSource);
		}

		return $q.all(sources).then(function (data) {
			return data.filter(function (s) {
				return s !== null;
			});
		});

		function loadInfo(sourceDescriptor, forRuntime, modelDescriptor) {
			if (typeof forRuntime === 'undefined') {
				forRuntime = true;
			}

			if (sourceDescriptor.OperationId || sourceDescriptor.WorkflowId) {
				return getServiceOperationSource(sourceDescriptor, isEmail);
			}

			if (sourceDescriptor.EntityName) {
				return getEntitySource(sourceDescriptor, modelDescriptor);
			}

			if (sourceDescriptor.DataDefinition) {
				return getDataDefinitionSource(sourceDescriptor, modelDescriptor, isEmail);
			}

			if (sourceDescriptor.DataQueryId) {
				return getDataQuerySource(sourceDescriptor);
			}

			return $q.when(null);

			function getServiceOperationSource(sourceDescriptor, isEmail) {
				let promise;

				if (sourceDescriptor.RequestDescriptor && sourceDescriptor.ResponseDescriptor) {
					promise = $q.when(sourceDescriptor);
				} else if (sourceDescriptor.WorkflowId) {
					promise = helpService.getWorkflowInfo(sourceDescriptor.WorkflowId);
				} else if (sourceDescriptor.OperationId) {
					promise = helpService.getOperationInfo(sourceDescriptor.OperationId);
				} else {
					promise = $q.when();
				}

				return promise.then(function (data) {
					const source = new mx.SolutionBuilderAgent.DataModelSource(sourceDescriptor, data && data.RequestContract, null, forRuntime);
					source.isOperation = !!sourceDescriptor.OperationId;
					source.isWorkflow = !!sourceDescriptor.WorkflowId;

					if (!data) {
						return makeSourceRemoved(source);
					}

					sourceDescriptor.HttpMethod = mx.shell.HttpMethod.find(data.HttpMethod);
					sourceDescriptor.RelativePath = data.RelativePath;
					sourceDescriptor.Operation = sourceDescriptor.Operation || data.ActionName;
					sourceDescriptor.ServiceId = sourceDescriptor.ServiceId || data.ServiceId;
					sourceDescriptor.Service = sourceDescriptor.Service || data.ServiceName;
					sourceDescriptor.BaseUrl = sourceDescriptor.BaseUrl || data.BaseUrl;

					if (!isEmail) {
						source.addSourceState();
						source.addInactive();
					}

					const response = data.ResponseContract;

					if (response && response.ContractDescriptor && response.ContractDescriptor.Type !== 'System_Void') {
						source.addProperty(
							mx.SolutionBuilderAgent.DataModelSystemProperty.Return,
							response.ContractDescriptor,
							response.UsedTypes,
							mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
						);
					}

					return source;
				});
			}

			function getEntitySource(sourceDescriptor, modelDescriptor) {
				if (!sourceDescriptor.EntityName) {
					throw new Error(internationalization.get('solutionBuilderAgent.errors.entity_name_not_provided'));
				}

				return helpService.getCiInfo(sourceDescriptor.EntityName, true).then(
					function (structure) {
						sourceDescriptor.HttpMethod = mx.shell.HttpMethod.Get;
						const entitySource = new mx.SolutionBuilderAgent.DataModelSource(sourceDescriptor, null, null, forRuntime);
						entitySource.isCi = true;
						if (!structure) {
							return makeSourceRemoved(entitySource);
						}
						entitySource.addGuid(mx.SolutionBuilderAgent.DataModelSystemProperty.ObjectId, { Direction: mx.SolutionBuilderAgent.DataModelPropertyDirection.IN });

						entitySource.addSourceState();

						entitySource.addInactive();

						entitySource.addProperty(
							sourceDescriptor.EntityName,
							structure.ContractDescriptor,
							structure.UsedTypes,
							mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
						);

						adjustEntityProperties(entitySource, modelDescriptor.ExtraProperties);

						return entitySource;
					},
					function (error) {
						return $q.reject(error);
					}
				);
			}

			function adjustFilter(sourceDescriptor, modelDescriptor, dataModelSource) {
				const sourcePath = sourceDescriptor.Path;
				const filterPath = `${sourcePath}.${mx.SolutionBuilderAgent.DataModelSystemProperty.Filter}`;
				(modelDescriptor.Properties || []).forEach((property) => {
					if (property.Path === filterPath || property.Parameters.some((parameter) => parameter.path === filterPath)) {
						dataModelSource.addFilter();
					}
				});
			}

			async function adjustFilterExpressionParameters(sourceDescriptor, modelDescriptor, dataModelSource) {
				const submitProperty = (property) => {
					const paramDataModel = dataModelSource.addSimpleProperty(
						mx.SolutionBuilderAgent.DataModelSystemProperty.Parameters,
						mx.SolutionBuilderAgent.DataModelTypes.ObjectType.name,
						mx.SolutionBuilderAgent.DataModelPropertyDirection.IN,
						null
					);
					paramDataModel.addSimpleProperty(property.Name, property.Type, mx.SolutionBuilderAgent.DataModelPropertyDirection.IN, { isArray: property.IsArray });
				};
				const handleProperty = (result) => {
					if (result.IsValid) {
						result.Parameters?.forEach((prop) => {
							submitProperty({
								Name: prop.Name.slice(1),
								Type: dataTypes[prop.ParameterType],
								IsArray: prop.IsArray,
							});
						});
					}
				};

				if (forRuntime && Array.isArray(modelDescriptor.Properties)) {
					const sourcePath = sourceDescriptor.Path;
					const parametersPath = `${sourcePath}.${mx.SolutionBuilderAgent.DataModelSystemProperty.Parameters}`;
					modelDescriptor.Properties.forEach((property) => {
						if (property.ParentPath === parametersPath) {
							submitProperty(property);
						}
					});
				} else if (sourceDescriptor.DataDefinition) {
					return filterExpressionService
						.getParametersForExpressionEntityClass(sourceDescriptor.FilterExpression, sourceDescriptor.DataDefinition)
						.then((result) => handleProperty(result));
				} else if (sourceDescriptor.DataQueryId) {
					return filterExpressionService
						.getParametersForExpressionDataQuery(sourceDescriptor.FilterExpression, sourceDescriptor.DataQueryId)
						.then((result) => handleProperty(result));
				}
			}

			function getDataDefinitionSource(sourceDescriptor, modelDescriptor, isEmail) {
				return helpService.getDdInfo(sourceDescriptor.DataDefinition, true).then(
					function (structure) {
						const dataDefinitionSource = new mx.SolutionBuilderAgent.DataModelSource(sourceDescriptor, null, null, forRuntime);
						dataDefinitionSource.isDd = true;
						if (!structure) {
							return makeSourceRemoved(dataDefinitionSource);
						}

						const keepLegacyId =
							isEmail ||
							(modelDescriptor.Properties || []).some(
								(prop) =>
									(prop.Name === mx.SolutionBuilderAgent.DataModelSystemProperty.Id && prop.ParentPath === sourceDescriptor.Path) ||
									prop.Parameters.some((parameter) => parameter.path === `${sourceDescriptor.Path}.${mx.SolutionBuilderAgent.DataModelSystemProperty.Id}`)
							);

						if (!sourceDescriptor.IsArray && keepLegacyId) {
							dataDefinitionSource.addProperty(
								mx.SolutionBuilderAgent.DataModelSystemProperty.Id,
								{
									Type: 'GuidType',
									Description: dataDefinitionSource.sysPropertyDescriptions.fragmentIdDescription,
								},
								{},
								mx.SolutionBuilderAgent.DataModelPropertyDirection.IN
							);
						}

						if (!isEmail) {
							dataDefinitionSource.addSourceState();
							dataDefinitionSource.addOrderBy();
							dataDefinitionSource.addInactive();

							dataDefinitionSource.addPagingData();
						} else {
							dataDefinitionSource.addProperty(
								'$objectId',
								{
									Type: 'GuidType',
									Description: dataDefinitionSource.sysPropertyDescriptions.fragmentIdDescription,
								},
								{},
								mx.SolutionBuilderAgent.DataModelPropertyDirection.IN
							);
							dataDefinitionSource.addFilter({ Expression: '' });
							dataDefinitionSource.addOrderBy({ Expression: '' });
						}

						const returnProperty = dataDefinitionSource.addProperty(
							structure.ContractDescriptor,
							{ Type: structure.ContractDescriptor },
							structure.UsedTypes,
							mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
						);

						returnProperty.isArray = sourceDescriptor.IsArray;
						adjustEntityProperties(dataDefinitionSource, modelDescriptor.ExtraProperties);

						if (!isEmail && sourceDescriptor.FilterExpression) {
							return adjustFilterExpressionParameters(sourceDescriptor, modelDescriptor, dataDefinitionSource).then(() => dataDefinitionSource);
						} else {
							adjustFilter(sourceDescriptor, modelDescriptor, dataDefinitionSource);
						}

						return dataDefinitionSource;
					},
					function (error) {
						return $q.reject(error);
					}
				);
			}

			function getDataQuerySource(sourceDescriptor) {
				return helpService.getDqInfo(sourceDescriptor.DataQueryId, true).then(function (structure) {
					const dataQuerySource = new mx.SolutionBuilderAgent.DataModelSource(sourceDescriptor, null, null, forRuntime);
					dataQuerySource.isDataQuery = true;
					if (!structure) {
						return makeSourceRemoved(dataQuerySource);
					}

					if (sourceDescriptor.FilterExpression === undefined) {
						dataQuerySource.addSimpleProperty(
							mx.SolutionBuilderAgent.DataModelSystemProperty.ObjectId,
							'GuidType',
							mx.SolutionBuilderAgent.DataModelPropertyDirection.IN
						);
						dataQuerySource.addSimpleProperty(
							mx.SolutionBuilderAgent.DataModelSystemProperty.FragmentIds,
							'GuidType',
							mx.SolutionBuilderAgent.DataModelPropertyDirection.IN,
							{ IsArray: true }
						);
					}

					const firstProperty = dataQuerySource.addProperty(
						mx.SolutionBuilderAgent.DataModelSystemProperty.First,
						{ Type: structure.ContractDescriptor },
						structure.UsedTypes,
						mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
					);

					firstProperty.isArray = false;

					dataQuerySource.addSourceState();
					dataQuerySource.addInactive();

					dataQuerySource.addPagingData();

					const returnProperty = dataQuerySource.addProperty(
						mx.SolutionBuilderAgent.DataModelSystemProperty.Return,
						{ Type: structure.ContractDescriptor },
						structure.UsedTypes,
						mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
					);

					returnProperty.isArray = true;

					if (sourceDescriptor.FilterExpression) {
						return adjustFilterExpressionParameters(sourceDescriptor, modelDescriptor, dataQuerySource).then(() => dataQuerySource);
					} else {
						adjustFilter(sourceDescriptor, modelDescriptor, dataQuerySource);
					}
					return dataQuerySource;
				});
			}
		}
	}

	function makeSourceRemoved(source) {
		source.loadOnInit = false;
		source.reloadable = false;
		source.removed = true;
		return source;
	}

	function getDataModel(options, widgetId, inputParameters) {
		const dataModelDefer = $q.defer();
		const dataModel = new mx.SolutionBuilderAgent.DataModel();
		const isEmail = Array.isArray(inputParameters);

		const modelDescriptor = options instanceof mx.SolutionBuilderAgent.DataModelDescriptor ? options : new mx.SolutionBuilderAgent.DataModelDescriptor(options);
		const extendedProperties = modelDescriptor.Properties;

		modelDescriptor.widgetId = widgetId;

		modelDescriptor.Properties.forEach(function (itm) {
			itm.Direction = undefined;
		});

		getSources(modelDescriptor, false, isEmail).then(function (sources) {
			dataModel.addSources(sources);
			dataModel.sources.some(function (source) {
				if (source.multiCurrencies) {
					dataModel.multiCurrencies = true;
					return true;
				}
				return false;
			});

			const isDialog = !options.dataQueryId && Boolean(options.entityName);
			const isWizard = !options.dataQueryId && Boolean(options.isWizard);

			const contextSource = isEmail ? getInputParametersModel(inputParameters, modelDescriptor.ExtraProperties) : $q.when(createContextSource(isWizard, isDialog));
			contextSource.then(function (context) {
				dataModel.addSources([context]);
				dataModelDefer.resolve(extendDataModel(dataModel, extendedProperties));
			});
		});

		return dataModelDefer.promise;

		function createContextSource(isWizard, isDialog) {
			const contextSource = new mx.SolutionBuilderAgent.DataModelSource({
				StaticSource: true,
				Path: 'Context',
				Name: 'Context',
				Properties: [],
			});

			contextSource.path = 'Context';
			contextSource.isStatic = true;

			contextSource.addCurrentUser();
			contextSource.addApplicationName();

			if (isWizard || isDialog) {
				contextSource.addActionContext(isDialog, isWizard);

				if (isWizard) {
					contextSource.addObjectReference('ObjectReference');
					contextSource.addGuid('ObjectIds', { IsArray: true });
					contextSource.addObjectReference('ObjectReferences', '', { IsArray: true });
				}
			}

			if (isDialog) {
				contextSource.addOuterContext();
			}

			if (!isWizard) {
				contextSource.addSharedContext();
			}

			contextSource.addNavigationId();
			contextSource.addFilterId();

			return contextSource;
		}
	}

	function cleanFromEmptyOptionalDataDefinition(object) {
		const result = angular.copy(object);
		if (typeof object === 'object' && object !== null && !Array.isArray(object)) {
			Object.keys(object).forEach((key) => {
				const value = object[key];
				if (typeof value === 'object' && value !== null && value.__optionalDD) {
					const hasProperties = Object.keys(value).some((propertyKey) => {
						const property = value[propertyKey];
						if (property instanceof mx.SolutionBuilderAgent.Relation) {
							if (property.AddedRelations.length || property.RemovedRelations.length) {
								return true;
							}
						} else {
							return true;
						}
					});
					if (!hasProperties) {
						delete result[key];
					}
				}
			});
		}
		return result;
	}

	function loadDialogDataModel(entityName, entity, context, options, freeDataSourcePromise, isService, staticData, defaultData) {
		staticData = staticData || {
			Context: context,
		};
		staticData[entityName] = entity;

		return (
			isService // if entity is not service then extend entity with default values from helper
				? $q.when()
				: getEntityModel(entityName, false).then(function (entitySource) {
						/* Predefine DataMode dialogs. If there is no data in data from server then define:
						 * 	1. {} for optional DD. It is necessary for calc-fields;
						 * 	2. empty MultiFragmentGrid from entityDefaultModel for optional multi-DD. For relations and other complex properties;
						 * 	3. init Relations properties with relation-config recursively;
						 */

						const entityDefaultModel = dataModelHelper.createModelDefaultObject([entitySource])[entityName];

						Object.keys(entityDefaultModel).forEach((key) => {
							const value = entityDefaultModel[key];
							const optionalDD = {};
							Object.defineProperty(optionalDD, '__optionalDD', {
								enumerable: false,
								value: true,
							});
							if (typeof value === 'object' && typeof entity[key] === 'undefined') {
								entity[key] = Array.isArray(value) ? value : optionalDD;
							}
						});

						dataModelHelper.initRelations(entity, entityDefaultModel);
						// init multifragment
				  })
		).then(function () {
			return loadDataModel(options, staticData, defaultData, freeDataSourcePromise).then(function (dataModel) {
				dataModel.entityName = entityName;
				return dataModel;
			});
		});
	}

	function loadDataModel(options, staticData, defaultData, freeDataSourcePromise) {
		const output = {
			sources: [],
			result: {},
		};

		output.result.$calcProperties = new mx.SolutionBuilderAgent.DataModelCalculatedPropertiesInfo();

		let modelDescriptor;

		if (!options) {
			modelDescriptor = new mx.SolutionBuilderAgent.DataModelDescriptor({
				Sources: [],
				Properties: [],
				ExtraProperties: [],
			});
			options = {};
		} else if (!(options instanceof mx.SolutionBuilderAgent.DataModelDescriptor)) {
			if (typeof options === 'string') {
				modelDescriptor = JSON.parse(options);
			}
			if (typeof options === 'object') {
				modelDescriptor = new mx.SolutionBuilderAgent.DataModelDescriptor(options);
			}
		} else {
			modelDescriptor = options;
		}

		if (options.dataQueryId) {
			modelDescriptor.dataQueryId = options.dataQueryId;
		}
		if (options.widgetId) {
			modelDescriptor.widgetId = options.widgetId;
		}

		return $q.all([userProfileService.get(), appsService.getCurrentApplication()]).then(function (result) {
			const userProfile = result[0];
			const currentApp = result[1];

			_.set(output.result, 'Context.CurrentUser', userProfile.id);
			_.set(output.result, 'Context.IsDevMode', userProfile.isDevMode);
			_.set(output.result, 'Context.ApplicationName', currentApp.name);
			_.set(output.result, 'Context.NavigationId', $stateParams.navigationId);
			_.set(output.result, 'Context.FilterId', $stateParams.filterId);

			return getSources(modelDescriptor, true).then(async function (sources) {
				output.sources = sources;
				if (sources.length) {
					angular.extend(output.result, dataModelHelper.createModelDefaultObject(sources, defaultData));
				}
				_.forEach(staticData || {}, function (staticValue, key) {
					if (Object.prototype.hasOwnProperty.call(output.result, key)) {
						angular.extend(output.result[key], staticData[key]);
					} else {
						_.set(output.result, key, staticValue);
					}
				});

				// TO DO: tmp fix. Direction to be properly saved in LD
				dataModelHelper.updateDirections(output.sources, modelDescriptor.Properties);

				await dataModelHelper.applyCalculatedProperties(output.result, modelDescriptor.Properties);

				return loadSources(output.result, output.sources, freeDataSourcePromise);
			});
		});
	}

	function loadSources(result, sources, freeDataSourcePromise) {
		_sourceDescriptors[result.Context.WidgetId] = sources;

		const promises = [];

		sources.forEach(function (source) {
			if (source.loadOnInit) {
				promises.push(
					$q.when().then(function () {
						reloadSource(source, result).then(
							function (data) {
								source.callBackPromise = null;
								return data;
							},
							function (error) {
								if (error !== 'by condition') {
									window.mx.console.warn("mxAgent: reloadSource() was rejected. Check whether it's ok. ", error);
								}
							}
						);
					})
				);
			}
			if (source.reloadable) {
				source.items.forEach(function (prop) {
					setCalcPropertyCallback(source, result, prop);
					if (prop.name === mx.SolutionBuilderAgent.DataModelSystemProperty.Parameters) {
						prop.items.forEach(function (child) {
							setCalcPropertyCallback(source, result, child);
						});
					}
				});
			}

			// Regular DataSource update with some interval
			if (source.refreshInterval && source.refreshInterval > 0 && freeDataSourcePromise) {
				freeDataSourcePromise.__intervalsToBeCleared = freeDataSourcePromise.__intervalsToBeCleared || [];

				const intervalID = window.setInterval(function () {
					reloadSource(source, result).then(
						function (data) {
							source.callBackPromise = null;
							return data;
						},
						function (error) {
							if (error !== 'by condition') {
								window.mx.console.warn("mxAgent: reloadSource() was rejected. Check whether it's ok. ", error);
							}
						}
					);
				}, source.refreshInterval * 1000);

				freeDataSourcePromise.__intervalsToBeCleared.push(intervalID);

				freeDataSourcePromise.then(function () {
					do {
						window.clearInterval(freeDataSourcePromise.__intervalsToBeCleared.pop());
					} while (freeDataSourcePromise.__intervalsToBeCleared.length > 0);
				});
			}
		});

		if (!promises.length) {
			promises.push($q.when(1));
		}
		return $q.all(promises).then(
			function () {
				return result;
			},
			function () {
				return result;
			}
		);
	}

	function setCalcPropertyCallback(source, result, prop) {
		if (!prop || prop.direction !== mx.SolutionBuilderAgent.DataModelPropertyDirection.IN) {
			return;
		}
		const calcProperties = result.$calcProperties;
		let calcProperty = calcProperties[prop.path];
		if (!calcProperty) {
			calcProperty = new mx.SolutionBuilderAgent.DataModelCalculatedProperty({
				Name: prop.name,
				ParentPath: prop.parentPath,
				Path: prop.path,
			});
			calcProperty.register(result).apply(result);
		}
		calcProperty.onChange('reload_' + source.path, function () {
			sourceCallBack(source, result, calcProperty.descriptor.Path);
		});
	}

	function sourceCallBack(source, model, triggeredBy) {
		// Reload service should be started after all callbacks was called
		// Thus we create $q.when().then(... (will be executed after current thread ended)
		source.callBackPromise =
			source.callBackPromise ||
			$q.when().then(function () {
				return reloadSource(source, model, undefined, triggeredBy).then(
					function (data) {
						source.callBackPromise = null;
						return data;
					},
					function (msg) {
						profiler.sourceRejected(source.name, msg);
						source.callBackPromise = null;
					}
				);
			});
	}

	function _isEmpty(id) {
		return !id || id === '00000000-0000-0000-0000-000000000000';
	}

	function reloadSource(source, model, headers, triggeredBy) {
		let url;
		let queryObject;
		const sourceStateName = sysProperties.SourceState;
		const status = mx.SolutionBuilderAgent.SourceLoadingStatus;
		let isExec = true;

		const sourceData = model[source.path];
		const context = model.Context;
		const inactive = sourceData[sysProperties.Inactive] === true;

		profiler.sourceTriggered(source.name, inactive, triggeredBy, sourceData);

		if (inactive) {
			sourceData[sourceStateName] = status.LOAD_REQUESTED;
			isExec = false;
		} else if (source.dataDefinition) {
			const alias = source.path + '.' + source.dataDefinition;

			url = 'api/WidgetCommon/' + context.WidgetId + '/DataSourceData/' + alias;

			let filter = null;

			if (!_isEmpty(sourceData.$id)) {
				filter = "id='" + sourceData.$id + "'";
			} else if (!_isEmpty(sourceData.$filter)) {
				filter = sourceData.$filter;
			} else if (!_isEmpty(sourceData.$parameters) || !_isEmpty(source.FilterExpression)) {
				url = `api/WidgetCommon/${context.WidgetId}/${source.id}/${alias}`;
				source.httpMethod = mx.shell.HttpMethod.Post;

				const parameters = getParametersList(source, sourceData);
				source.bodyParameter = ['Parameters'];
				sourceData.Parameters = JSON.stringify(parameters);
			}

			if (filter !== null && filter.length > 0) {
				url += '?filter=' + encodeURIComponent(filter);
			}

			if (sourceData[sysProperties.OrderBy]) {
				url += (url.indexOf('?') === -1 ? '?orderby=' : '&orderby=') + encodeURIComponent(sourceData[sysProperties.OrderBy]);
			}

			if (sourceData[sysProperties.PageSize]) {
				url += (url.indexOf('?') === -1 ? '?pageSize=' : '&pageSize=') + sourceData[sysProperties.PageSize];
				url += '&page=' + (sourceData[sysProperties.PageNumber] || 0);
			}

			// Execute or skip service depending on filter/parameters existence
			isExec = url.includes('?filter=') || (source.bodyParameter || []).length > 0 ? true : false;
		} else if (source.entityName) {
			if (_isEmpty(sourceData.ObjectId)) {
				isExec = false;
			} else {
				url = 'api/WidgetCommon/GetData/' + context.WidgetId + '/' + sourceData.ObjectId + '?alias=' + source.path + '.' + source.entityName;
			}
		} else if (source.dataQueryId) {
			url = 'api/DataQuery/' + source.dataQueryId;
			if (!_isEmpty(sourceData.ObjectId)) {
				url += '/' + sourceData.ObjectId;
			} else if (Array.isArray(sourceData.FragmentIds) && sourceData.FragmentIds.length) {
				source.httpMethod = mx.shell.HttpMethod.Post;
				source.bodyParameter = ['FragmentIdsExpression'];

				sourceData.FragmentIdsExpression =
					'{"Conditions":[{"Value":["ID in (\'' + sourceData.FragmentIds.join("','") + '\')","Expression by ids"],"IsExpression":true}],"LogicalOperator":1}';
			} else if (!_isEmpty(sourceData.$filter)) {
				source.httpMethod = mx.shell.HttpMethod.Post;
				source.bodyParameter = ['FilterDataExpression'];

				sourceData.FilterDataExpression = '{"Conditions":[{"Value":["' + sourceData.$filter + '","Filter expression"],"IsExpression":true}],"LogicalOperator":1}';
			} else if (!_isEmpty(sourceData.$parameters) || !_isEmpty(source.FilterExpression)) {
				url = `api/DataQuery/${context.WidgetId}/${source.id}/query`;
				source.httpMethod = mx.shell.HttpMethod.Post;

				const parameters = getParametersList(source, sourceData);
				source.bodyParameter = ['Parameters'];
				sourceData.Parameters = `{"Parameters": ${JSON.stringify(parameters)}, "LogicalOperator": 1}`;
			}

			if (sourceData[sysProperties.OrderBy]) {
				url += '?orderby=' + encodeURIComponent(sourceData[sysProperties.OrderBy]);
			}

			if (sourceData[sysProperties.PageSize]) {
				const paramPrefix = url.indexOf('?') === -1 ? '?' : '&';
				url += paramPrefix + 'pageSize=' + sourceData[sysProperties.PageSize] + '&page=' + (sourceData[sysProperties.PageNumber] || 0);
			}
		} else if (source.relativePath) {
			let extraPath;
			const extraQueryObject = {};
			if (sourceData.$options) {
				const arrOpts = sourceData.$options.split('&');
				arrOpts.forEach(function (opt) {
					const optKeyIdx = opt.indexOf('=');
					if (optKeyIdx !== -1) {
						const optKey = opt.substring(0, optKeyIdx);
						extraQueryObject[optKey] = opt.substring(optKeyIdx + 1);

						if (source.relativePath.lastIndexOf('&') !== -1) {
							extraPath += '&';
						}
						extraPath = (extraPath || '') + optKey + '={' + optKey + '}';
					}
				});

				const idxExtraPath = source.relativePath.indexOf(extraPath);
				if (idxExtraPath !== -1) {
					source.relativePath = source.relativePath.substring(0, idxExtraPath);
				}
			}

			const urlCfg = dataModelHelper.preparePath(source.relativePath, sourceData, source);

			if (sourceData.$options) {
				source.relativePath += extraPath;
				Object.keys(extraQueryObject).forEach(function (extQueryNm) {
					urlCfg.queryObject[extQueryNm] = extraQueryObject[extQueryNm];
				});
			}

			isExec = urlCfg.execute;
			queryObject = urlCfg.queryObject;
			url = urlCfg.path;
		}

		return isExec ? executeSource(url, source, model, source.bodyParameter, queryObject, headers) : $q.reject('by condition');

		function executeSource(url, source, model, bodyParameter, queryObject, headers) {
			if (queryObject && typeof queryObject === 'object') {
				Object.keys(queryObject).forEach(function (key) {
					if (queryObject[key] instanceof Date) {
						queryObject[key] = queryObject[key].toISOString();
					}
				});
			}

			const sourceData = model[source.path] || {};

			sourceData[sourceStateName] = status.LOADING;

			const method = source.httpMethod;
			const defer = $q.defer();
			const host = http.host(source.host);
			let httpCall;
			const isDownload = source.downloadSource === true;

			if (isDownload) {
				headers = headers || {};
				headers.responseType = 'arraybuffer';
			}

			const skipNotifications = Boolean(sourceData.$catchError);

			if (method === mx.shell.HttpMethod.Post || method === mx.shell.HttpMethod.Put) {
				let bodyObject = {};

				if (bodyParameter.length === 1) {
					bodyObject = sourceData[bodyParameter[0]];
				} else if (bodyParameter.length > 1) {
					bodyParameter.forEach(function (paramName) {
						angular.extend(bodyObject, sourceData[paramName]);
					});
				}
				let methodName = method === mx.shell.HttpMethod.Post ? 'post' : 'put';
				if (bodyObject instanceof ArrayBuffer || bodyObject instanceof File /*file upload*/) {
					methodName = 'upload';
				}
				const httpCallFn = host[methodName].bind(host);

				profiler.sourceExecuting(source.name, url, {
					method: '#' + methodName,
					bodyObject: bodyObject,
					queryObject: queryObject,
					headers: headers,
				});

				httpCall = httpCallFn(url, bodyObject, headers, queryObject, skipNotifications, true);
			} else if (method === mx.shell.HttpMethod.Get) {
				profiler.sourceExecuting(source.name, url, {
					method: '#get',
					queryObject: queryObject,
					headers: headers,
				});

				httpCall = host.get(url, queryObject, headers, skipNotifications, true);
			} else if (method === mx.shell.HttpMethod.Delete) {
				profiler.sourceExecuting(source.name, url, {
					method: '#delete',
					queryObject: queryObject,
					headers: headers,
				});

				httpCall = host.remove(url, queryObject, headers, skipNotifications, false);
			}

			if (httpCall) {
				httpCall.then(
					function (response) {
						if (isDownload) {
							if (downloadHelpService.downloadContent(response, response.data)) {
								sourceData[sourceStateName] = status.LOADED;
								sourceData.$errorMessage = '';
								defer.resolve();
							} else {
								sourceData[sourceStateName] = status.REJECTED;
								sourceData.$errorMessage = internationalization.get('solutionBuilderAgent.errors.no_file_data');
								defer.reject(internationalization.get('solutionBuilderAgent.errors.no_file_data'));
							}
						} else {
							backgroundProcessesService.addTrackedProcess(response.data);
							dataModelHelper.applySourceResult(model, response.data, source, response);
							sourceData.$errorMessage = '';
							sourceData[sourceStateName] = status.LOADED;
							defer.resolve();
						}
					},
					function (error) {
						sourceData[sourceStateName] = status.REJECTED;
						sourceData.$errorMessage = error.message;
						defer.reject(error.message);
						if (!sourceData.$catchError) {
							return $q.reject(error);
						}
					}
				);
			} else {
				sourceData[sourceStateName] = status.REJECTED;
				sourceData.$errorMessage = internationalization.get('solutionBuilderAgent.errors.method_not_called');
				defer.reject(internationalization.get('solutionBuilderAgent.errors.method_not_called'));
			}
			return defer.promise;
		}
	}

	function getParametersList(source, sourceData) {
		const parametersSource = source && source.items && source.items.find((prop) => prop.name === mx.SolutionBuilderAgent.DataModelSystemProperty.Parameters);
		const parametersTypeMap = parametersSource
			? parametersSource.items.map((param) => {
					return {
						paramName: param.name,
						// todo: there is a problem with types conversion while saving the parameters from model (see Assign Model -> Take Value From)
						paramType: param.type === 'ObjectReference' ? dataTypes.GuidType : dataTypes[param.type],
					};
			  })
			: [];
		return sourceData && sourceData.$parameters
			? Object.keys(sourceData.$parameters).map((param) => {
					const mapping = parametersTypeMap.find((map) => map.paramName === param);
					return {
						Name: `@${param}`,
						Value: sourceData.$parameters[param],
						Type: mapping ? mapping.paramType : dataTypes['Unknown'],
					};
			  })
			: [];
	}

	function extendSourceResult(model, applyPath, parent, property, data) {
		dataModelHelper.initRelations(data, parent[property]);
		model.$calcProperties.$$merge(parent, property, data, applyPath);
		model.$calcProperties.$$applyTo(applyPath, model, dataModelHelper.additionalData, true);
	}

	function reloadSourceByPath(sourceName, dataModel, widgetId, requestHeaders) {
		const headers = requestHeaders || {};
		const sourcesDescriptor = _sourceDescriptors[widgetId];

		if (sourceName && sourcesDescriptor) {
			let targetSource = null;

			sourcesDescriptor.some(function (sd) {
				if (sd.path === sourceName) {
					targetSource = sd;
					return true;
				}
			});

			if (targetSource) {
				return reloadSource(targetSource, dataModel, headers);
			}
		}
		return $q.when(null);
	}

	function createStaticSource(structure, forRuntime, specialOptions) {
		const descriptor = new mx.SolutionBuilderAgent.DataModelSourceDescriptor(
			prepareContractOptions(structure.ContractDescriptor, mx.SolutionBuilderAgent.DataModelPropertyDirection.BOTH)
		);

		return new mx.SolutionBuilderAgent.DataModelSource(descriptor, null, structure, forRuntime, specialOptions);
	}

	function prepareContractOptions(contract, contractDirection) {
		if (contract) {
			const res = { Direction: contractDirection || mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT };

			if (typeof contract === 'string') {
				res.Name = 'Data Query Model';
				res.Path = 'DataQuery';
				res.HttpMethod = mx.shell.HttpMethod.Get;
			} else if (contract.Type) {
				res.Name = contract.Name || contract.Type;
				res.Path = contract.Path || res.Name;
				res.HttpMethod = contract.HttpMethod || mx.shell.HttpMethod.Get;
			} else {
				return null;
			}
			res.StaticSource = true;
			return res;
		}
		return null;
	}

	function extendDataModel(model, extensions) {
		if (extensions && extensions.length && model && model.sources) {
			extensions.forEach(function (propertyExt) {
				model.sources.some(function (source) {
					const parent = source.find(propertyExt.ParentPath || propertyExt.ParentName, source.removed);

					if (parent) {
						parent.addProperty(propertyExt.Name, angular.copy(propertyExt), { Extended: true }, mx.SolutionBuilderAgent.DataModelPropertyDirection.NONE);
						return true;
					}
				});
			});
		}
		return model;
	}

	function extendDataModelSource(source, extensions) {
		if (extensions && extensions.length) {
			extensions.forEach(function (propertyExt) {
				const parent = source.find(propertyExt.ParentPath || propertyExt.ParentName, source.removed);

				if (parent) {
					parent.addProperty(propertyExt.Name, angular.copy(propertyExt), { Extended: true }, mx.SolutionBuilderAgent.DataModelPropertyDirection.NONE);
					return true;
				}
			});
		}
	}

	function saveCalculatedField(context, property, isExtra) {
		context._dataModelOptions = context._dataModelOptions || {};
		delete property.Direction;
		const propertiesObj = isExtra ? context._dataModelOptions.ExtraProperties : context._dataModelOptions.Properties;

		if (
			!propertiesObj.some(function (itm) {
				const itmPath = itm.ParentPath + '.' + itm.Name;

				if (itmPath === property.Path) {
					// Update
					itm.Parameters = property.Parameters;
					itm.Expression = property.Expression;
					itm.Description = property.Description;
					itm.EditMode = property.EditMode || 1;
					return true;
				}
			})
		) {
			// Add
			propertiesObj.push(new mx.SolutionBuilderAgent.DataModelPropertyDescriptor(property));
		}
	}

	function deleteCalculatedField(dataModelItem, context) {
		return context.dataModelPromise.then(function (dataModel) {
			if (
				(dataModel.sources || []).some(function (source) {
					return deleteFieldByPath(source, dataModelItem.path);
				})
			) {
				let i = 0,
					j = 0;

				if (
					context._dataModelOptions.Properties.some(function (property) {
						if (property.Path === dataModelItem.path) {
							return true;
						}
						i++;
					})
				) {
					context._dataModelOptions.Properties.splice(i, 1);

					context._dataModelOptions.Properties = context._dataModelOptions.Properties.filter(function (property) {
						return property.Path.indexOf(dataModelItem.path + '.') !== 0;
					});

					return true;
				} else if (
					context._dataModelOptions.ExtraProperties.some(function (property) {
						if (property.Path === dataModelItem.path) {
							return true;
						}
						j++;
					})
				) {
					context._dataModelOptions.ExtraProperties.splice(j, 1);
					return true;
				}
			}
			return false;
		});
	}

	function deleteFieldByPath(parent, path) {
		let deleted = false;
		let itemIndex;

		if (parent && parent.path && path) {
			if (parent.items) {
				itemIndex = 0;
				parent.items.some(function (item) {
					if (item.path === path) {
						if (item.overridden) {
							item.editMode = 0;
							item.expression = '';
						} else {
							parent.items.splice(itemIndex, 1);
						}
						deleted = true;
					} else {
						itemIndex++;
						deleted = deleteFieldByPath(item, path);
					}
					return deleted;
				});
			}
		}
		return deleted;
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DataModelService#getDataQueryModel
	 *
	 * @description
	 * Returns data source by  id
	 * @param {string} id - DataSource id
	 * @param {boolean} forRuntime - Flag indicating when we call current method, in design time or in runtime. 'true' by default
	 * @returns {promise} result - a promise that resolves data.
	 */
	function getDataQueryModel(id, forRuntime) {
		if (!id) {
			throw new Error(internationalization.get('solutionBuilderAgent.errors.dataquery_not_provided'));
		}
		if (typeof forRuntime === 'undefined') {
			forRuntime = true;
		}

		const key = 'DataQueryModel_' + id;
		const model = cache.data[key];

		return (
			model ||
			(cache.data[key] = $q.all([helpService.getDqInfo(id), columnDefinitionService.getGridDefinitionById(id)]).then(function (res) {
				const structure = res[0];
				const columnsInfo = res[1];

				const descriptor = new mx.SolutionBuilderAgent.DataModelSourceDescriptor(
					prepareContractOptions(structure.ContractDescriptor, mx.SolutionBuilderAgent.DataModelPropertyDirection.BOTH)
				);

				let requestContract = null;

				if (columnsInfo.ServiceInfo) {
					const operationInfo = _.find(columnsInfo.ServiceInfo.Operations, { SupportedOperation: 4 });

					if (operationInfo) {
						requestContract = operationInfo.RequestContract;
						// Remove oData parameters
						const requestParams = requestContract.UsedTypes.RequestContract.Descriptor;
						const keys = [];

						_.forEach(requestParams, function (value, key) {
							if (value.Type && value.Type.indexOf('System_Web_Http_OData') === 0) {
								keys.push(key);
							}
						});
						keys.forEach(function (key) {
							delete requestParams[key];
						});
					}
				}
				const source = new mx.SolutionBuilderAgent.DataModelSource(descriptor, requestContract, null, forRuntime);

				source.addSourceState();
				source.addInactive();

				const response = structure;

				if (response && response.ContractDescriptor && response.ContractDescriptor.Type !== 'System_Void') {
					const returnProperty = source.addProperty(
						mx.SolutionBuilderAgent.DataModelSystemProperty.Return,
						{
							Type: response.ContractDescriptor,
							IsArray: true,
						},
						response.UsedTypes,
						mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT
					);

					returnProperty.addSimpleProperty(mx.SolutionBuilderAgent.DataModelSystemProperty.Item, 'ObjectType', mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT, {
						Title: 'Data Query Item',
					});
				}
				return source;
			}))
		);
	}

	function getInputParametersModel(inputParameters, extraProperties) {
		const promises = [];

		const source = new mx.SolutionBuilderAgent.DataModelSource(
			{
				StaticSource: true,
				Path: 'Parameters',
				Name: 'Parameters',
				Properties: [],
			},
			null,
			null,
			false
		);
		source.isStatic = true;

		inputParameters.forEach(function (property) {
			const entityType = property.Type === mx.SolutionBuilderAgent.DataModelTypes.Entity.id && mx.SolutionBuilderAgent.DataModelTypes.Entity.name;
			const dataDefinitionType = property.Type === mx.SolutionBuilderAgent.DataModelTypes.DataDefinition.id && mx.SolutionBuilderAgent.DataModelTypes.DataDefinition.name;

			if (entityType || dataDefinitionType) {
				promises.push(
					helpService[entityType ? 'getCiInfo' : 'getDdInfo'](property.EntityName).then(
						function (structure) {
							const options = angular.extend(property, {
								Type: property.EntityName,
								DataDefinition: dataDefinitionType ? property.EntityName : undefined,
							});
							const dataModelProperty = source.addProperty(property.Name, options, structure.UsedTypes, mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT);
							dataModelProperty.type = property.EntityName;
						},
						function (error) {
							return $q.reject(error);
						}
					)
				);
			} else {
				source.addProperty(property.Name, property, null, mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT);
			}
		});

		return $q.all(promises).then(function () {
			adjustEntityProperties(source, extraProperties);
			return source;
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.DataModelService#getEntityModel
	 *
	 * @description
	 * Returns business object fields its type
	 * @param {string} entityName - object type
	 * @param {boolean} forRuntime - Flag indicating when we call current method, in design time or in runtime. 'true' by default
	 * @param {array} extraProperties - Array of extra server properties for entity source
	 * @returns {promise} result - a promise that resolves data.
	 */
	function getEntityModel(entityName, forRuntime, extraProperties) {
		if (!entityName) {
			throw new Error(internationalization.get('solutionBuilderAgent.errors.entity_name_not_provided'));
		}
		if (typeof forRuntime === 'undefined') {
			forRuntime = true;
		}

		return helpService.getCiInfo(entityName).then(
			function (structure) {
				const res = createStaticSource(structure, forRuntime, { entityName: entityName });

				adjustEntityProperties(res, extraProperties);
				return res;
			},
			function (error) {
				return $q.reject(error);
			}
		);
	}

	function adjustEntityProperties(source, extraProperties) {
		if (Array.isArray(source.items)) {
			source.items.forEach(function (prop) {
				adjustEntityProperties(prop);
			});
		}
		if (source.type && source.type === 'VirtualRelationEnd' && source.relationsConfig.otherSideIsMany()) {
			source.addSimpleProperty(
				mx.SolutionBuilderAgent.DataModelSystemProperty.Count,
				mx.SolutionBuilderAgent.DataModelTypes.IntType.name,
				mx.SolutionBuilderAgent.DataModelPropertyDirection.NONE,
				{ Description: source.name + ' Count' }
			);
		}
		if (source.entityName) {
			source.items.forEach(function (entityClassItem) {
				if (entityClassItem.isArray) {
					entityClassItem.isMultiFragment = true;
				}
			});
		}
		if (extraProperties && extraProperties.length) {
			extraProperties.forEach(function (prop) {
				const parent = source.find(prop.ParentPath);

				if (parent) {
					prop.Direction = mx.SolutionBuilderAgent.DataModelPropertyDirection.OUT;
					prop.ParentItem = parent;
					const extraItem = new mx.SolutionBuilderAgent.DataModelProperty(prop);

					parent.items.push(extraItem);
				}
			});
		}
	}

	function getCustomWidgetModel(controlDescriptorId) {
		const attributesSource = new mx.SolutionBuilderAgent.DataModelSource({
			StaticSource: true,
			Path: 'WidgetAttributes',
			Name: 'Widget Attributes',
			Properties: [],
		});

		attributesSource.path = 'WidgetAttributes';
		attributesSource.isStatic = true;

		return controlDescriptorService.getProperties(controlDescriptorId).then(function (properties) {
			properties.forEach(function (property) {
				attributesSource.addProperty(
					property.camelCasedName,
					{
						Name: property.camelCasedName,
						Title: property.title,
						Direction: property.direction,
						IsArray: property.isArray,
						Type: property.type,
					},
					null,
					property.direction
				);
			});

			return attributesSource;
		});
	}
}
